/**
 * What this account has run, and what is left of it.
 *
 * The page exists for one question, which arrives as a support email rather
 * than a feature request: where did my result go. Results are kept until
 * somebody deletes them now, so the answer is usually "here", and this page
 * is also where deleting happens.
 *
 * Nothing here resurrects anything. A held result can be reopened, which is
 * just the ordinary poll against a job that still has one; a deleted result
 * offers no Open button, since a control that cannot work is worse than an
 * absent one. Deleting asks first, because it cannot be undone.
 */

import { useEffect, useState } from "preact/hooks";
import { ApiError, type Api } from "../api";
import { download } from "../exports";
import type { RunRow } from "../types";

export interface RunsProps {
  api: Api;
  /** Reopen a single run whose result the API still holds. */
  onOpen: (run: RunRow) => void;
  onExit: () => void;
}

export function Runs({ api, onOpen, onExit }: RunsProps) {
  const [runs, setRuns] = useState<RunRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .runs()
      .then((body) => {
        if (!live) return;
        setRuns(body.runs);
      })
      .catch((failure) => {
        if (!live) return;
        setError(
          failure instanceof ApiError || failure instanceof Error
            ? failure.message
            : "The history could not be read.",
        );
      });
    return () => {
      live = false;
    };
  }, [api]);

  /** Reflect a deletion locally rather than re-fetching the whole list. */
  function markDeleted(match: (run: RunRow) => boolean) {
    setRuns(
      (current) =>
        current?.map((run) =>
          match(run) ? { ...run, result_available: false } : run,
        ) ?? null,
    );
  }

  async function remove(run: RunRow) {
    const many = run.source === "batch";
    const question = many
      ? `Delete every result in this batch? "${run.document}" came from a folder, and the whole batch goes together. This cannot be undone.`
      : `Delete the result for "${run.document}"? This cannot be undone.`;
    if (!confirm(question)) return;
    setError(null);
    setNotice(null);
    setBusy(run.reference);
    try {
      const cleared = await api.deleteRun(run.reference);
      markDeleted((row) => row.reference === run.reference);
      setNotice(
        cleared === 1
          ? "Result deleted. The run stays in your history."
          : `${cleared} results deleted. The runs stay in your history.`,
      );
    } catch (failure) {
      setError(describe(failure, "The result could not be deleted."));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Drop where a run's tracts were placed, and keep the extraction.
   *
   * The narrow half of delete. Placing is hand work and a wrong placement is
   * worth undoing on its own, without paying to extract the deed again.
   */
  async function forget(run: RunRow) {
    if (
      !confirm(
        `Forget where "${run.document}" was placed? The extraction stays; reopening it will start at the origin again.`,
      )
    )
      return;
    setError(null);
    setNotice(null);
    setBusy(run.reference);
    try {
      const forgotten = await api.forgetPlacements(run.reference);
      setRuns(
        (current) =>
          current?.map((row) =>
            row.reference === run.reference ? { ...row, placements: 0 } : row,
          ) ?? null,
      );
      setNotice(`${forgotten} placement(s) forgotten. The results stay.`);
    } catch (failure) {
      setError(describe(failure, "The placements could not be forgotten."));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Take every parcel this account has located, as one file.
   *
   * The reason this lives on the history page rather than beside a document's
   * own exports: it is not about a document. Eleven deeds in one county are
   * eleven separate GeoPackages otherwise, each in its own local plane with
   * its point of beginning at the origin, which is the right answer for one
   * instrument and the wrong one for a chain of title.
   */
  async function fabric() {
    setError(null);
    setNotice(null);
    setBusy("fabric");
    try {
      download(await api.fabric(), "easting-fabric.gpkg");
      setNotice("Fabric downloaded: every parcel this account has located.");
    } catch (failure) {
      setError(
        describe(
          failure,
          "The fabric could not be assembled. Place a tract first, in the browser or in QGIS.",
        ),
      );
    } finally {
      setBusy(null);
    }
  }

  async function purge() {
    if (
      !confirm(
        "Delete every result this account still holds? The history stays, but every extraction goes. This cannot be undone.",
      )
    )
      return;
    setError(null);
    setNotice(null);
    setBusy("all");
    try {
      const cleared = await api.purgeRuns();
      markDeleted(() => true);
      setNotice(`${cleared} result(s) deleted. Your history stays.`);
    } catch (failure) {
      setError(describe(failure, "The results could not be deleted."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="page">
      <h2>Run history</h2>
      <p class="muted">
        Every document this account has run, across all of its keys. Results are
        kept until you delete them. A run whose result you have deleted stays
        listed, because the record outlives the result.
      </p>

      {error && <p class="banner banner-fail">{error}</p>}
      {notice && <p class="banner">{notice}</p>}
      {runs === null && !error && <p class="muted">Reading the history…</p>}
      {runs?.length === 0 && (
        <p class="muted">
          Nothing yet. Documents you run in the browser, through the plugin, or
          over the API all land here.
        </p>
      )}

      {!!runs?.length && (
        <table class="calls runs">
          <thead>
            <tr>
              <th>Document</th>
              <th>Submitted</th>
              <th>Where</th>
              <th>Status</th>
              <th>Result</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <Row
                key={run.id}
                run={run}
                onOpen={() => onOpen(run)}
                // Every run the account owns, whichever key ran it. The
                // collect routes answer for the person now, so a deed
                // extracted in QGIS opens here with the placement it was
                // given there.
                openable={run.result_available}
                deletable={run.result_available}
                busy={busy === run.reference}
                onDelete={() => remove(run)}
                onForget={() => forget(run)}
              />
            ))}
          </tbody>
        </table>
      )}

      <p class="runs-actions">
        <button type="button" class="ghost" onClick={onExit}>
          Back
        </button>
        {!!runs?.some((run) => run.result_available) && (
          <button
            type="button"
            class="ghost"
            disabled={busy !== null}
            onClick={fabric}
            title="Every parcel this account has located, in one WGS 84 GeoPackage."
          >
            {busy === "fabric" ? "Assembling…" : "Download fabric"}
          </button>
        )}
        {!!runs?.some((run) => run.result_available) && (
          <button
            type="button"
            class="ghost danger"
            disabled={busy !== null}
            onClick={purge}
            title="Clears every result on this account. The history stays."
          >
            Delete all results
          </button>
        )}
      </p>
    </div>
  );
}

function Row({
  run,
  onOpen,
  openable,
  deletable,
  busy,
  onDelete,
  onForget,
}: {
  run: RunRow;
  onOpen: () => void;
  openable: boolean;
  deletable: boolean;
  busy: boolean;
  onDelete: () => void;
  onForget: () => void;
}) {
  return (
    <tr>
      <td>{run.document}</td>
      <td>{when(run.submitted_at)}</td>
      <td class="muted">{run.source === "batch" ? "Folder" : "Single"}</td>
      <td>
        {run.status}
        {run.error && <div class="muted small">{run.error}</div>}
      </td>
      <td class="muted">
        {resultText(run)}
        {!!run.placements && (
          <div
            class="muted small"
            title="Reopening draws them where you put them."
          >
            {run.placements} placed
          </div>
        )}
      </td>
      <td>
        {openable && (
          <button
            type="button"
            class="link"
            onClick={onOpen}
            // A batch is collected whole or not at all: the results route
            // serves every document in it. So the label says "batch" rather
            // than pretending one document can be opened on its own. Hiding
            // the button instead, which is what this did until 2026-08-13,
            // left a row that offered nothing and explained nothing.
            title={
              run.source === "batch"
                ? "Collects the whole batch and opens this document."
                : "Opens this result."
            }
          >
            {run.source === "batch" ? "Open batch" : "Open"}
          </button>
        )}
        {!!run.placements && (
          <button
            type="button"
            class="link"
            disabled={busy}
            onClick={onForget}
            title="Forgets where this run's tracts were placed. The extraction stays."
          >
            Forget placement
          </button>
        )}
        {deletable && (
          <button
            type="button"
            class="link danger"
            disabled={busy}
            onClick={onDelete}
            title={
              run.source === "batch"
                ? "Deletes every result in this batch. The runs stay listed."
                : "Deletes this result. The run stays listed."
            }
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        )}
      </td>
    </tr>
  );
}

/** What the API holds, in the words a person would use about it. */
function resultText(run: RunRow): string {
  if (run.status === "failed") return "none";
  if (run.result_available) return "held";
  if (run.status === "succeeded") return "deleted";
  return "not finished";
}

function when(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

function describe(failure: unknown, fallback: string): string {
  if (failure instanceof ApiError || failure instanceof Error)
    return failure.message;
  return fallback;
}
